'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useMemo } from 'react'

import { useWidgetOfType } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { usePdpColor } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { protoToProduct } from '@src/components/widgets/product-purchase/product-purchase.adapter'
import {
  productPurchaseV2ToSvdWire,
  svdToProduct,
} from '@src/components/widgets/product-purchase-v2/product-purchase-v2.adapter'

import StyliticsRecommendationPlLegacy from './stylitics-recommendation-pl-legacy'

interface StyliticsRecommendationPlProps {
  /**
   * The Shop-the-Look reco band to render when Stylitics resolves with no outfits (≤2), fails to
   * load, or the page has no product purchase data (e.g. bundle) — the same PairWithProducts
   * fallback contract as the non-PL `CompleteTheLookWrapper`, expressed as the PL band node.
   */
  fallback: React.ReactNode
}

/**
 * PL adapter/router for the Stylitics "Shop the Look" Composer widget. The legacy (non-PL) module
 * reads its product identity + selected color from the PDP redux (`pdpStateManager`), which the PL
 * pipeline does not populate — this router resolves the PL equivalents instead:
 *   - product identity: the PRODUCT_PURCHASE (or SVD) widget's proto payload via the page-widget
 *     store (`useWidgetOfType`), adapted with `protoToProduct` so telemetry productProperties
 *     (spid/title/tags/subClassId) match the non-PL events byte-for-byte.
 *   - selected color: the selection bus (`usePdpColor`), published by the buy box on every swatch
 *     change — replacing the legacy redux `selectedOptions` read.
 * Presentation is deliberately NOT routed by `shouldUseDesignSystem()`: the experiment launches on
 * the legacy-styled fork across all four PL/non-PL x DS/non-DS branches (the shared DS block is
 * parked until it passes UAT; the legacy SCSS renders correctly on panda pages via its
 * `--legacy-rem` shim). Restore the DS branch here (and in the non-PL `CompleteTheLookWrapper`)
 * once the DS fork is UAT'd.
 */
const StyliticsRecommendationPl = ({ fallback }: StyliticsRecommendationPlProps) => {
  const purchaseEntry = useWidgetOfType(WidgetType.PRODUCT_PURCHASE)
  // The SVD buy box moved to the proto PRODUCT_PURCHASE_V2 widget with a VDA-native payload
  // (STRFRNT-15385) — its domain Product comes via the v2 wire adapters, not `protoToProduct`.
  const v2PurchaseEntry = useWidgetOfType(WidgetType.PRODUCT_PURCHASE_V2)
  const purchaseData = purchaseEntry?.widget.data?.productPurchaseData
  const v2PurchaseData = v2PurchaseEntry?.widget.data?.productPurchaseV2Data
  const product = useMemo(() => {
    if (purchaseData) return protoToProduct(purchaseData)
    // TELEMETRY GAP (SVD only): the V2 buy-box payload has no product-level `tags` field at all
    // (see `ProductPurchaseV2WidgetData` in the proto registry — `classification` is there, `tags`
    // is not; the only `tags` on the SVD domain is per-variant `SvdActiveInventory.tags`, an
    // unrelated pre-order shape). So `productProperties.tags` — which non-PL and the proto
    // PRODUCT_PURCHASE path both carry via `mapTags` — is absent on SVD PDPs for every Stylitics
    // event (widget impressions, outfit-click IS_VISIBLE, CTL/item clicks). It cannot be resolved
    // client-side: closing it needs `tags` added to the V2 purchase payload by the BFF.
    if (v2PurchaseData) return svdToProduct(productPurchaseV2ToSvdWire(v2PurchaseData))
    return undefined
  }, [purchaseData, v2PurchaseData])
  const selectedColor = usePdpColor()
  // "Page has a buy box at all", NOT "the product resolved yet" — the legacy fork uses it to tell
  // the bundle/no-buy-box case (render the bare fallback, no composer host) apart from a buy box
  // whose selection is still resolving.
  const hasPurchaseData = Boolean(purchaseData ?? v2PurchaseData)

  return (
    <StyliticsRecommendationPlLegacy
      product={product}
      selectedColor={selectedColor}
      fallback={fallback}
      hasPurchaseData={hasPurchaseData}
    />
  )
}

export default StyliticsRecommendationPl
